import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import SimplePeer from "simple-peer";
import { Video, Circle, User } from "lucide-react";

// Import refactored components
import { CallControls, VideoPanel, CallHistory, PatientList } from "@/components/telehealth";

const ALLOWED_ROLES = ["admin", "administrator", "staff", "nurse", "coordinator"];

export default function Telehealth() {
  const { t } = useTranslation();
  const { user, token, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  // State
  const [loading, setLoading] = useState(true);
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [callState, setCallState] = useState("idle"); // idle, calling, connected, ended
  const [isMuted, setIsMuted] = useState(false);
  const [isCameraOff, setIsCameraOff] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [callDuration, setCallDuration] = useState(0);
  const [callNotes, setCallNotes] = useState("");
  const [showEndCallDialog, setShowEndCallDialog] = useState(false);
  const [callHistory, setCallHistory] = useState([]);
  const [wsConnected, setWsConnected] = useState(false);
  
  // Screen Sharing State
  const [isScreenSharing, setIsScreenSharing] = useState(false);
  const screenStreamRef = useRef(null);
  
  // Multi-Party Call State
  const [participants, setParticipants] = useState([]);
  const [showAddParticipantDialog, setShowAddParticipantDialog] = useState(false);
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const peersRef = useRef({});
  const remoteStreamsRef = useRef({});

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const peerRef = useRef(null);
  const wsRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const recordingChunksRef = useRef([]);
  const callTimerRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const videoPanelRef = useRef(null);

  // Check auth and initialize
  useEffect(() => {
    if (authLoading) return;
    
    if (!user || !ALLOWED_ROLES.includes(user.role?.toLowerCase())) {
      navigate("/", { replace: true });
      return;
    }
    
    loadPatients();
    loadCallHistory();
    initializeMedia();

    return () => cleanup();
  }, [user, navigate, authLoading]);

  const loadPatients = async () => {
    try {
      // Try the assignments endpoint first (new system)
      const res = await api.get("/assignments/my-patients").catch(() => ({ data: { patients: [] } }));
      const assignedPatients = res.data.patients || [];
      
      // Transform to standard format for telehealth
      const patients = assignedPatients.map(p => ({
        id: p.patient_id || p.id,
        patient_name: p.patient_name || p.patient_details?.name || "Unknown",
        name: p.patient_name || p.patient_details?.name || "Unknown",
        diagnosis: p.patient_details?.diagnosis || p.diagnosis || "",
        caregiver_id: p.patient_details?.caregiver_id
      }));
      
      setPatients(patients);
    } catch (error) {
      console.error("Error loading patients:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadCallHistory = async () => {
    try {
      const res = await api.get("/telehealth/history").catch(() => ({ data: { calls: [] } }));
      setCallHistory(res.data.calls || []);
    } catch (error) {
      console.error("Error loading call history:", error);
    }
  };

  const initializeMedia = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
      });
      
      localStreamRef.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
      }
      
      initializeWebSocket();
      toast.success("Camera and microphone ready");
    } catch (error) {
      console.error("Media error:", error);
      toast.error("Failed to access camera/microphone. Please check permissions.");
    }
  };

  const initializeWebSocket = () => {
    const wsUrl = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://");
    const ws = new WebSocket(`${wsUrl}/api/telehealth/signal/${token}`);
    
    ws.onopen = () => setWsConnected(true);
    ws.onmessage = (event) => handleSignalingMessage(JSON.parse(event.data));
    ws.onclose = () => setWsConnected(false);
    ws.onerror = (error) => console.error("WebSocket error:", error);
    wsRef.current = ws;
  };

  const handleSignalingMessage = (data) => {
    const handlers = {
      call_accepted: () => peerRef.current?.signal(data.signal),
      call_rejected: () => { toast.error("Call was declined"); cleanup(); setCallState("idle"); },
      answer: () => peerRef.current?.signal(data.signal),
      ice_candidate: () => peerRef.current?.signal({ candidate: data.candidate }),
      call_ended: () => { toast.info("Call ended by remote user"); endCall(); },
      participant_joined: () => handleParticipantSignal(data),
      participant_signal: () => handleParticipantSignal(data),
      participant_left: () => { removeParticipant(data.participant_id); toast.info(`${data.participant_name || 'A participant'} left`); }
    };
    handlers[data.type]?.();
  };

  const startCall = async (patient) => {
    if (!localStreamRef.current) {
      toast.error("Camera not ready");
      return;
    }

    setSelectedPatient(patient);
    setCallState("calling");
    callStartTimeRef.current = new Date();

    const peer = new SimplePeer({
      initiator: true,
      trickle: true,
      stream: localStreamRef.current,
      config: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] }
    });

    peer.on("signal", (signal) => {
      wsRef.current?.send(JSON.stringify({
        type: "call_initiate",
        target_user_id: patient.caregiver_id || patient.id,
        signal,
        patient_name: patient.patient_name || patient.name
      }));
    });

    peer.on("stream", (remoteStream) => {
      if (remoteVideoRef.current) remoteVideoRef.current.srcObject = remoteStream;
      setCallState("connected");
      startCallTimer();
      toast.success("Call connected");
    });

    peer.on("error", (err) => { console.error("Peer error:", err); toast.error("Connection error"); endCall(); });
    peer.on("close", () => console.log("Peer connection closed"));

    peerRef.current = peer;

    // Simulate call acceptance for demo
    setTimeout(() => {
      if (callState === "calling") {
        setCallState("connected");
        startCallTimer();
      }
    }, 3000);
  };

  const startCallTimer = () => {
    callTimerRef.current = setInterval(() => setCallDuration(prev => prev + 1), 1000);
  };

  // Toggle functions
  const toggleMute = () => {
    localStreamRef.current?.getAudioTracks().forEach(track => track.enabled = !track.enabled);
    setIsMuted(!isMuted);
  };

  const toggleCamera = () => {
    localStreamRef.current?.getVideoTracks().forEach(track => track.enabled = !track.enabled);
    setIsCameraOff(!isCameraOff);
  };

  const toggleScreenShare = async () => {
    if (isScreenSharing) {
      screenStreamRef.current?.getTracks().forEach(track => track.stop());
      screenStreamRef.current = null;
      
      const cameraTrack = localStreamRef.current?.getVideoTracks()[0];
      if (peerRef.current && cameraTrack) {
        const sender = peerRef.current._pc?.getSenders?.().find(s => s.track?.kind === 'video');
        if (sender) await sender.replaceTrack(cameraTrack);
      }
      
      setIsScreenSharing(false);
      toast.info("Screen sharing stopped");
    } else {
      try {
        const screenStream = await navigator.mediaDevices.getDisplayMedia({ video: { cursor: "always" }, audio: false });
        screenStreamRef.current = screenStream;
        
        const screenTrack = screenStream.getVideoTracks()[0];
        if (peerRef.current) {
          const sender = peerRef.current._pc?.getSenders?.().find(s => s.track?.kind === 'video');
          if (sender) await sender.replaceTrack(screenTrack);
        }
        
        screenTrack.onended = () => toggleScreenShare();
        setIsScreenSharing(true);
        toast.success("Screen sharing started");
      } catch (error) {
        if (error.name !== 'AbortError') toast.error("Failed to share screen");
      }
    }
  };

  const toggleRecording = () => {
    if (isRecording) {
      mediaRecorderRef.current?.stop();
      setIsRecording(false);
    } else {
      startRecording();
    }
  };

  const startRecording = () => {
    if (!localStreamRef.current) return;

    const options = { mimeType: "video/webm;codecs=vp9,opus" };
    try {
      const recorder = new MediaRecorder(localStreamRef.current, options);
      recordingChunksRef.current = [];
      
      recorder.ondataavailable = (e) => { if (e.data.size > 0) recordingChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(recordingChunksRef.current, { type: "video/webm" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `telehealth-call-${Date.now()}.webm`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success("Recording saved");
      };
      
      recorder.start(1000);
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      toast.success("Recording started");
    } catch (error) {
      toast.error("Recording failed");
    }
  };

  // Multi-party functions
  const handleParticipantSignal = (data) => {
    const { from, signal, participant_name } = data;
    
    if (peersRef.current[from]) {
      peersRef.current[from].signal(signal);
    } else {
      const peer = new SimplePeer({
        initiator: false,
        trickle: true,
        stream: localStreamRef.current,
        config: { iceServers: [{ urls: "stun:stun.l.google.com:19302" }] }
      });
      
      peer.on("signal", (sig) => wsRef.current?.send(JSON.stringify({ type: "participant_signal", target_user_id: from, signal: sig })));
      peer.on("stream", (remoteStream) => {
        remoteStreamsRef.current[from] = remoteStream;
        setParticipants(prev => {
          const existing = prev.find(p => p.id === from);
          if (existing) return prev.map(p => p.id === from ? { ...p, stream: remoteStream, connected: true } : p);
          return [...prev, { id: from, name: participant_name, stream: remoteStream, connected: true }];
        });
      });
      
      peer.signal(signal);
      peersRef.current[from] = peer;
    }
  };

  const removeParticipant = (participantId) => {
    peersRef.current[participantId]?.destroy();
    delete peersRef.current[participantId];
    delete remoteStreamsRef.current[participantId];
    setParticipants(prev => prev.filter(p => p.id !== participantId));
  };

  const endCall = () => {
    if (callTimerRef.current) clearInterval(callTimerRef.current);
    
    wsRef.current?.send(JSON.stringify({ type: "call_end", target_user_id: selectedPatient?.caregiver_id || selectedPatient?.id }));
    
    // Save call record
    if (selectedPatient && callStartTimeRef.current) {
      api.post("/telehealth/log", {
        patient_id: selectedPatient.id,
        patient_name: selectedPatient.patient_name || selectedPatient.name,
        duration_seconds: callDuration,
        notes: callNotes,
        recording_available: isRecording
      }).catch(console.error);
    }

    peerRef.current?.destroy();
    Object.values(peersRef.current).forEach(peer => peer.destroy());
    
    setCallState("idle");
    setSelectedPatient(null);
    setCallDuration(0);
    setCallNotes("");
    setParticipants([]);
    peersRef.current = {};
    
    if (isScreenSharing) toggleScreenShare();
    if (isRecording) toggleRecording();
    
    loadCallHistory();
    setShowEndCallDialog(false);
  };

  const cleanup = () => {
    localStreamRef.current?.getTracks().forEach(track => track.stop());
    screenStreamRef.current?.getTracks().forEach(track => track.stop());
    wsRef.current?.close();
    peerRef.current?.destroy();
    Object.values(peersRef.current).forEach(peer => peer.destroy());
    if (callTimerRef.current) clearInterval(callTimerRef.current);
  };

  // Loading and auth states
  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6" data-testid="telehealth-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold flex items-center gap-3">
          <Video className="w-7 h-7 text-primary" />
          {t("telehealth.title")}
        </h1>
        <p className="text-muted-foreground">{t("telehealth.subtitle")}</p>
        <div className="flex items-center gap-2 mt-2">
          <Badge variant={wsConnected ? "default" : "secondary"} className={wsConnected ? "bg-green-500" : ""}>
            <Circle className={`w-2 h-2 mr-1 ${wsConnected ? "fill-current" : ""}`} />
            {wsConnected ? t("telehealth.connected") : t("telehealth.connecting")}
          </Badge>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Video Area */}
        <div className="lg:col-span-2 space-y-4">
          <Card>
            <CardContent className="p-0">
              <VideoPanel
                ref={videoPanelRef}
                callState={callState}
                callDuration={callDuration}
                isRecording={isRecording}
                isCameraOff={isCameraOff}
                isFullscreen={isFullscreen}
                selectedPatient={selectedPatient}
                participants={participants}
                onToggleFullscreen={() => setIsFullscreen(!isFullscreen)}
                localVideoRef={localVideoRef}
                remoteVideoRef={remoteVideoRef}
              />
              
              <CallControls
                callState={callState}
                isMuted={isMuted}
                isCameraOff={isCameraOff}
                isScreenSharing={isScreenSharing}
                isRecording={isRecording}
                onToggleMute={toggleMute}
                onToggleCamera={toggleCamera}
                onToggleScreenShare={toggleScreenShare}
                onToggleRecording={toggleRecording}
                onAddParticipant={() => setShowAddParticipantDialog(true)}
                onEndCall={() => setShowEndCallDialog(true)}
              />
            </CardContent>
          </Card>

          {/* Patient Info During Call */}
          {selectedPatient && callState !== "idle" && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-medium">{selectedPatient.patient_name || selectedPatient.name}</h3>
                    <p className="text-sm text-muted-foreground">{selectedPatient.diagnosis || t("telehealth.consultation")}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {callState === "idle" && (
            <PatientList patients={patients} onStartCall={startCall} />
          )}
          
          <CallHistory calls={callHistory} />
          
          {/* Quick Tips */}
          <Card className="bg-green-50 dark:bg-green-950 border-green-200">
            <CardContent className="p-4">
              <h4 className="font-medium text-green-800 dark:text-green-200 mb-2">{t("telehealth.tips.title")}</h4>
              <ul className="text-sm text-green-700 dark:text-green-300 space-y-1">
                <li>• {t("telehealth.tips.lighting")}</li>
                <li>• {t("telehealth.tips.headphones")}</li>
                <li>• {t("telehealth.tips.testConnection")}</li>
                <li>• {t("telehealth.tips.recording")}</li>
              </ul>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* End Call Dialog */}
      <Dialog open={showEndCallDialog} onOpenChange={setShowEndCallDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("telehealth.endCall")}</DialogTitle>
            <DialogDescription>{t("telehealth.addNotes")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>{t("telehealth.callNotes")}</Label>
            <Textarea
              placeholder={t("telehealth.notesPlaceholder")}
              value={callNotes}
              onChange={(e) => setCallNotes(e.target.value)}
              rows={4}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEndCallDialog(false)}>{t("telehealth.cancel")}</Button>
            <Button variant="destructive" onClick={endCall}>{t("telehealth.endCall")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Participant Dialog */}
      <Dialog open={showAddParticipantDialog} onOpenChange={setShowAddParticipantDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("telehealth.addParticipant")}</DialogTitle>
            <DialogDescription>{t("telehealth.inviteDescription")}</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>{t("telehealth.selectParticipant")}</Label>
            <Select onValueChange={(value) => {
              const participant = patients.find(p => p.id === value);
              if (participant) {
                setParticipants(prev => [...prev, { ...participant, connected: false }]);
                setShowAddParticipantDialog(false);
                toast.success(t("common.loading"));
              }
            }}>
              <SelectTrigger className="mt-2">
                <SelectValue placeholder={t("telehealth.chooseParticipant")} />
              </SelectTrigger>
              <SelectContent>
                {patients.filter(p => p.id !== selectedPatient?.id).map(p => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.patient_name || p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddParticipantDialog(false)}>{t("telehealth.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
